/**
 * Export and Transshipment Risk: the entity service's combined risk evaluation
 * (POST business-checks/evaluate-entity-risk). It runs these agents in
 * parallel, each a live web search, and each saves its latest result to the
 * record's `<agent>_info` / `<agent>_checked_at` columns. Product end use and
 * vertical tenure join only when a product is in context, which a record page
 * never has, so they are not listed here.
 */

import moment from 'moment';

export type RiskAgent =
  | 'export_control_risk'
  | 'adverse_media'
  | 'financial_risk'
  | 'personnel_expertise';

export type RiskLevel = 'Low' | 'Medium' | 'High';

export const RISK_AGENTS: Array<{
  key: RiskAgent;
  label: string;
  note: string;
}> = [
  {
    key: 'export_control_risk',
    label: 'Export control',
    note: 'diversion & transshipment',
  },
  { key: 'adverse_media', label: 'Adverse media', note: 'public sources' },
  {
    key: 'financial_risk',
    label: 'Financial risk',
    note: 'standing & solvency',
  },
  {
    key: 'personnel_expertise',
    label: 'Personnel',
    note: 'people & expertise',
  },
];

export interface RiskFinding {
  claim?: string;
  source?: string;
  url?: string;
  date?: string | null;
  severity?: RiskLevel;
  /** The agent's own category, e.g. 'transshipment'; varies by agent. */
  category?: string;
  confidence?: number;
}

/** One agent's answer from a run; 'not-run' when the run did not include it. */
export interface RiskAgentResult {
  agent: RiskAgent;
  status: 'answered' | 'failed' | 'not-run';
  risk: RiskLevel | null;
  summary: string;
  riskDriver: string;
  findings: RiskFinding[];
  confidence: number | null;
  checkedAt: string | null;
  error?: string;
}

export interface RiskEvaluation {
  overallRisk: RiskLevel | null;
  agents: RiskAgentResult[];
  /** When the run finished; null when no agent answered. */
  checkedAt: string | null;
  answered: number;
  failed: number;
}

const SCORE: Record<string, number> = { Low: 1, Medium: 2, High: 3 };

const asRisk = (value: unknown): RiskLevel | null => {
  const text = String(value || '')
    .trim()
    .toLowerCase();
  if (text === 'high') return 'High';
  if (text === 'medium') return 'Medium';
  if (text === 'low') return 'Low';
  return null;
};

const parseInfo = (value: unknown): Record<string, any> | null => {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
  return typeof value === 'object' ? (value as Record<string, any>) : null;
};

const agentResult = (
  agent: RiskAgent,
  info: Record<string, any> | null,
  checkedAt: string | null,
): RiskAgentResult => ({
  agent: agent,
  status: info ? 'answered' : 'not-run',
  risk: asRisk(info?.overall_risk),
  summary: typeof info?.summary === 'string' ? info.summary : '',
  riskDriver: typeof info?.risk_driver === 'string' ? info.risk_driver : '',
  findings: Array.isArray(info?.findings) ? info?.findings || [] : [],
  confidence:
    typeof info?.confidence === 'number' && Number.isFinite(info.confidence)
      ? info.confidence
      : null,
  checkedAt: info ? checkedAt : null,
});

const scoreOf = (risk: RiskLevel | null) => (risk ? SCORE[risk] : 0);

// One run, one finish time: the verdict is the worst answer, the time is
// the run's, shown only when an agent answered.
const combine = (
  agents: RiskAgentResult[],
  checkedAt: string,
): RiskEvaluation => {
  let overallRisk: RiskLevel | null = null;
  for (const result of agents) {
    if (scoreOf(result.risk) > scoreOf(overallRisk)) overallRisk = result.risk;
  }
  const answered = agents.filter((result) => result.status === 'answered');
  return {
    overallRisk: overallRisk,
    agents: agents,
    checkedAt: answered.length ? checkedAt : null,
    answered: answered.length,
    failed: agents.filter((result) => result.status === 'failed').length,
  };
};

/** The last results saved on the record, or null when no agent has run. */
export const storedRiskEvaluation = (
  record: Record<string, any> | null | undefined,
): RiskEvaluation | null => {
  if (!record) return null;
  const agents = RISK_AGENTS.map(({ key }) =>
    agentResult(
      key,
      parseInfo(record[`${key}_info`]),
      record[`${key}_checked_at`] || null,
    ),
  );
  // Adverse media also runs on its own (screening, the screening tab's box), so
  // it alone does not count as an Export and Transshipment Risk run.
  const ranHere = agents.some(
    (result) =>
      result.agent !== 'adverse_media' && result.status === 'answered',
  );
  if (!ranHere) return null;
  // The agents save as they finish, so the run finished with the last of them.
  const checkedAt = agents.reduce<string | null>(
    (latest, result) =>
      result.checkedAt &&
      (!latest || moment(result.checkedAt).isAfter(latest))
        ? result.checkedAt
        : latest,
    null,
  );
  return combine(agents, checkedAt || '');
};

/** A fresh response from evaluate-entity-risk. */
export const responseRiskEvaluation = (
  response: Record<string, any> | null | undefined,
  checkedAt: string,
): RiskEvaluation => {
  const outcomes: any[] = Array.isArray(response?.outcomes)
    ? response?.outcomes || []
    : [];
  const agents = RISK_AGENTS.map(({ key }) => {
    const outcome = outcomes.find((item) => item?.agent === key);
    if (!outcome) return agentResult(key, null, null);
    if (outcome.status !== 'fulfilled') {
      return {
        ...agentResult(key, null, null),
        status: 'failed' as const,
        error: String(outcome.error || 'This agent did not answer.'),
      };
    }
    return agentResult(key, parseInfo(outcome.result) || {}, checkedAt);
  });
  return combine(agents, checkedAt);
};
